// SFX module backed by WebAudio. Logical names map onto physical mp3 files
// in src/assets/sfx/ (some names share a file as a sensible fallback).
// Loading uses fetch+decodeAudioData so playback latency is sub-ms once the
// buffer is cached. The shared AudioContext lives in audio/bus.ts and must
// be unlocked by a user gesture before sound is audible (browser autoplay).

import clueRevealUrl from '../assets/sfx/clue-reveal.mp3?url';
import timerTickUrl from '../assets/sfx/timer-tick.mp3?url';
import answerRightUrl from '../assets/sfx/answer-right.mp3?url';
import answerWrongUrl from '../assets/sfx/answer-wrong.mp3?url';
import dupRejectUrl from '../assets/sfx/dup-reject.mp3?url';
import scoreWhooshUrl from '../assets/sfx/score-whoosh.mp3?url';
import roundFanfareUrl from '../assets/sfx/round-fanfare.mp3?url';
import doublingStingUrl from '../assets/sfx/doubling-sting.mp3?url';
import podiumWinUrl from '../assets/sfx/podium-win.mp3?url';
import connectionLostUrl from '../assets/sfx/connection-lost.mp3?url';

import { audioBus } from './bus.js';

export type SfxName =
  | 'clue-reveal'
  | 'clue-type'
  | 'timer-warn'
  | 'answer-sting'
  | 'answer-wrong'
  | 'dup-reject'
  | 'score-whoosh'
  | 'round-fanfare'
  | 'doubling-sting'
  | 'podium'
  | 'connection-lost'
  | 'coin-flip'
  | 'calibration';

const SFX_URLS: Record<SfxName, string> = {
  'clue-reveal': clueRevealUrl,
  'clue-type': timerTickUrl,
  'timer-warn': timerTickUrl,
  'answer-sting': answerRightUrl,
  'answer-wrong': answerWrongUrl,
  'dup-reject': dupRejectUrl,
  'score-whoosh': scoreWhooshUrl,
  'round-fanfare': roundFanfareUrl,
  'doubling-sting': doublingStingUrl,
  podium: podiumWinUrl,
  'connection-lost': connectionLostUrl,
  'coin-flip': scoreWhooshUrl,
  calibration: clueRevealUrl,
};

const buffers = new Map<SfxName, AudioBuffer | null>();
const inflight = new Map<SfxName, Promise<AudioBuffer | null>>();

async function loadBuffer(name: SfxName): Promise<AudioBuffer | null> {
  if (buffers.has(name)) return buffers.get(name) ?? null;
  const existing = inflight.get(name);
  if (existing) return existing;
  const ctx = audioBus.context();
  if (!ctx) return null;
  const url = SFX_URLS[name];
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      buffers.set(name, buf);
      return buf;
    } catch (err) {
      console.warn(`[sfx] failed to load ${name} (${url}):`, err);
      buffers.set(name, null);
      return null;
    } finally {
      inflight.delete(name);
    }
  })();
  inflight.set(name, p);
  return p;
}

function playBuffer(ctx: AudioContext, buf: AudioBuffer, name: SfxName): void {
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(audioBus.sfxGain());
    src.start(0);
  } catch (err) {
    console.warn(`[sfx] play ${name} failed:`, err);
  }
}

export const sfx = {
  async preload(): Promise<void> {
    const names = Object.keys(SFX_URLS) as SfxName[];
    await Promise.all(names.map((n) => loadBuffer(n)));
  },

  play(name: SfxName): void {
    const ctx = audioBus.context();
    if (!ctx) {
      console.debug(`[sfx] ${name} (no AudioContext)`);
      return;
    }
    const buf = buffers.get(name);
    if (!buf) {
      void loadBuffer(name).then((b) => {
        if (b && audioBus.isUnlocked()) playBuffer(ctx, b, name);
      });
      console.debug(`[sfx] ${name} (lazy-loading)`);
      return;
    }
    if (!audioBus.isUnlocked()) {
      void audioBus.unlock();
    }
    playBuffer(ctx, buf, name);
  },

  _resetForTests(): void {
    buffers.clear();
    inflight.clear();
  },
};
